import React from 'react';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary' | 'primary-solid';
/** Accepts design-system variants plus legacy names for backward compat */
type BadgeVariantProp = BadgeVariant | 'default' | 'secondary' | 'outline' | 'destructive' | (string & {});
type BadgeSize = 'sm' | 'md';
type BadgeSizeProp = BadgeSize | 'xs';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariantProp;
  size?: BadgeSizeProp;
  dot?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Badge — SMMS Design System v2.0
 *
 * Semantic status badges. 6 variants, 2 sizes, optional dot prefix.
 * Always pill-shaped (rounded-full). No uppercase on content — only the badge text.
 */
export default function Badge({
  variant = 'neutral',
  size = 'sm',
  dot = false,
  children,
  className = '',
  ...props
}: BadgeProps) {
  const variantClasses: Record<BadgeVariant, string> = {
    success: 'bg-success-bg text-success-muted border-success/20',
    warning: 'bg-warning-bg text-warning-muted border-warning/20',
    danger:  'bg-danger-bg text-danger-muted border-danger/20',
    info:    'bg-info-bg text-info-muted border-info/20',
    // neutral ground is bg-elevated, NOT bg-bg: text-muted's AA contrast
    // was validated against surface-tier grounds, and on the darker bg-bg
    // tint it falls below AA (caught by the /setup sweep on locked-row
    // "Optional" pills, 2026-08-27 — the stacked-tints defect class; the
    // admin console's Badge default has always used bg-elevated and passes).
    neutral: 'bg-elevated text-muted border-border',
    primary: 'bg-primary-glass text-primary border-primary/20',
    // Opaque fill, not a tint. Every variant above is a translucent tint
    // (`bg-X-bg`/`bg-primary-glass`) validated for AA against a LIGHT
    // surface (packages/design-system/tokens/__tests__/contrast.test.ts) —
    // composited over a solid, already-saturated background (e.g. a
    // selected/filled tile) those tints under-contrast badly (RMI
    // questionnaire selected-score tile, 2026-09-27: `primary` on
    // `bg-primary-light` measured ~1.45:1). `bg-primary`/`text-on-primary`
    // is the same CI-locked pair already used for filled primary buttons
    // ("white text on primary" / "dark white-on-primary (button label)" in
    // that same contrast test, >=4.5:1 in both themes) — reuse it verbatim
    // instead of inventing a new pair.
    'primary-solid': 'bg-primary text-on-primary border-transparent',
  };

  const dotColors: Record<BadgeVariant, string> = {
    success: 'bg-success',
    warning: 'bg-warning',
    danger:  'bg-danger',
    info:    'bg-info',
    neutral: 'bg-muted',
    primary: 'bg-primary',
    'primary-solid': 'bg-on-primary',
  };

  const sizeClasses: Record<BadgeSize, string> = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  // Map legacy variant names
  const resolvedVariant: BadgeVariant =
    variant === 'default' ? 'neutral' :
    variant === 'secondary' ? 'neutral' :
    variant === 'outline' ? 'neutral' :
    variant === 'destructive' ? 'danger' :
    (['success', 'warning', 'danger', 'info', 'neutral', 'primary', 'primary-solid'].includes(variant as string)
      ? variant as BadgeVariant : 'neutral');

  const resolvedSize: BadgeSize =
    size === 'xs' ? 'sm' :
    (['sm', 'md'].includes(size as string) ? size as BadgeSize : 'sm');

  return (
    <span
      /* `smms-badge` is a stable, style-free hook (no rules target it in the
         normal cascade) for the forced-colors override in
         design-system/tokens/global.css — see A11Y-0006. Without it, the
         low-opacity variant border (e.g. `border-success/20`) can collapse
         to near-invisible once the background tint is stripped under
         Windows High Contrast Mode, leaving the chip boundary invisible. */
      className={`smms-badge inline-flex items-center gap-1 rounded-full border font-medium whitespace-nowrap ${
        variantClasses[resolvedVariant]
      } ${sizeClasses[resolvedSize]} ${className}`}
      {...props}
    >
      {dot && (
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotColors[resolvedVariant]}`} />
      )}
      {children}
    </span>
  );
}
